// One person, shown the same way wherever the admin or the brand portal shows
// someone (Brúnó, 2026-10-15: one profile for everyone, used wherever a person
// appears, in a chat or anywhere else): their picture, else their initials;
// their name; one line under it (their handle, where they are); and, where the
// page has a profile to open, one click on them opens it. Each app passes the
// picture addresses it may load. The admin passes the creator's photo from the
// app, then our copies of their social pictures. A brand passes its own
// checked /api/brand/creators/<id>/picture. So neither app reaches into the
// other's API.

import {useState} from 'react';
import type {ReactNode} from 'react';
import './person.css';

/** A name's initials: its first and last word, else its first two letters (a handle's too, as the admin's). */
export function personInitials(name: string): string {
  const bits = name.replace(/^@+/, '').trim().split(/\s+/).filter(Boolean);
  return (bits.length > 1 ? `${bits[0]![0]}${bits.at(-1)![0]}` : (bits[0] || '?').slice(0, 2)).toUpperCase();
}

/**
 * The first of `sources` that loads, else `fallback`. The failures are counted
 * per list of sources: when the addresses change (a new photo, a lookup's
 * ?v=), a picture that had given way to the fallback is asked for again.
 */
export function PersonPhoto({sources, className, fallback}: {
  sources: readonly string[];
  className?: string;
  fallback: ReactNode;
}) {
  const usable = sources.filter(Boolean);
  const key = usable.join('\n');
  const [failed, setFailed] = useState({key, count: 0});
  const tried = failed.key === key ? failed.count : 0;
  const src = usable[tried];
  if (!src) return <>{fallback}</>;
  return <img className={className} src={src} alt="" loading="lazy" decoding="async" onError={() => setFailed({key, count: tried + 1})} />;
}

export type PersonSize = 'sm' | 'md' | 'lg';

/**
 * A person in a row: the picture, the name, and a line under it with the
 * handle and `status`. With `onOpen`, the whole thing is a button that opens
 * their profile. Without it, it sits inside whatever the row is, such as a
 * thread to pick or a link.
 */
export function Person({name, handle = '', status, sources = [], size = 'md', onOpen, className = ''}: {
  name: string;
  handle?: string;
  status?: ReactNode;
  sources?: readonly string[];
  size?: PersonSize;
  onOpen?: () => void;
  className?: string;
}) {
  const at = handle.replace(/^@+/, '');
  const classes = ['ebc-person', size, className].filter(Boolean).join(' ');
  const body = (
    <>
      <PersonPhoto sources={sources} className="ebc-person-photo"
        fallback={<span className="ebc-person-photo" aria-hidden="true">{personInitials(name)}</span>} />
      <span className="ebc-person-text">
        <b>{name}</b>
        {at || status ? <small>{at ? `@${at}` : null}{at && status ? ' · ' : null}{status}</small> : null}
      </span>
    </>
  );
  return onOpen
    ? <button className={classes} type="button" onClick={onOpen} aria-label={`Open ${name}’s profile`}>{body}</button>
    : <span className={classes}>{body}</span>;
}
